import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Input, Modal, Space, Table } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import { GET_INFLUENCER_CATEGORIES } from 'api/influencerCategory/queries';
import { DELETE_INFLUENCER_CATEGORY } from 'api/influencerCategory/mutations';
// Types
import {
  GetInfluencerCategories,
  GetInfluencerCategories_getInfluencerCategories,
} from 'api/influencerCategory/types/GetInfluencerCategories';
import {
  DeleteInfluencerCategory,
  DeleteInfluencerCategoryVariables,
} from 'api/influencerCategory/types/DeleteInfluencerCategory';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CreateEditInfluencerCategory from './components/CreateEditInfluencerCategory/CreateEditInfluencerCategory';
// Styles
import styles from './ManageInfluencerCategories.module.scss';

const { confirm } = Modal;

type InfluencerCategory = GetInfluencerCategories_getInfluencerCategories;

const ManageInfluencerCategories = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRecord, setSelectedRecord] =
    useState<InfluencerCategory | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] =
    useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);

  const { data, loading } = useQuery<GetInfluencerCategories>(
    GET_INFLUENCER_CATEGORIES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteInfluencerCategory, { loading: deleteLoading }] = useMutation<
    DeleteInfluencerCategory,
    DeleteInfluencerCategoryVariables
  >(DELETE_INFLUENCER_CATEGORY, {
    refetchQueries: [{ query: GET_INFLUENCER_CATEGORIES }],
  });

  const influencerCategories = data?.getInfluencerCategories;
  const categories = useMemo(
    () => influencerCategories || [],
    [influencerCategories]
  );

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return categories;
    }

    return categories.filter((category) =>
      category.name.toLowerCase().includes(term)
    );
  }, [categories, searchTerm]);

  const openCreateModal = () => {
    setSelectedRecord(null);
    setIsCreateModalVisible(true);
  };

  const openEditModal = (record: InfluencerCategory) => {
    setSelectedRecord(record);
    setIsEditModalVisible(true);
  };

  const hideCreateEditModal = () => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setSelectedRecord(null);
  };

  const handleDelete = (record: InfluencerCategory) => {
    confirm({
      title: `Are you sure you want to delete "${record.name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteInfluencerCategory({
            variables: { id: record.id },
          });
          successNotification('Influencer category removed');
        } catch (error) {
          errorNotification(
            (error as Error)?.message ||
              'Looks like something went wrong. Please try again later.'
          );
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: InfluencerCategory, b: InfluencerCategory) =>
        a.name.localeCompare(b.name),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 200,
      render: function Actions(_: unknown, record: InfluencerCategory) {
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => openEditModal(record)}>
              Edit
            </Button>
            <Button danger onClick={() => handleDelete(record)}>
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const isEditing = !!selectedRecord && isEditModalVisible;
  const isModalVisible = isCreateModalVisible || isEditing;

  return (
    <div className={styles.root}>
      <PageToolbar
        leading={
          <Input.Search
            placeholder="Search by name"
            allowClear
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      >
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={openCreateModal}
        >
          Add influencer category
        </Button>
      </PageToolbar>

      <Table<InfluencerCategory>
        rowKey="id"
        columns={columns}
        dataSource={filteredCategories}
        loading={loading || deleteLoading}
        pagination={false}
        scroll={{ x: 600 }}
      />

      <Modal
        title={
          isEditing ? 'Edit influencer category' : 'New influencer category'
        }
        visible={isModalVisible}
        onCancel={hideCreateEditModal}
        footer={null}
        destroyOnClose
      >
        <CreateEditInfluencerCategory
          record={selectedRecord}
          onFinish={hideCreateEditModal}
        />
      </Modal>
    </div>
  );
};

export default ManageInfluencerCategories;
